import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiError } from "@/lib/auth";

export default function AdminLogin() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const from = location.state?.from || "/admin/leads";

  useEffect(() => {
    if (user) {
      navigate("/admin/leads", { replace: true });
    }
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Email and password are required.");
      return;
    }
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err, "Invalid email or password."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-white" data-testid="admin-login-page">
      {/* Left panel */}
      <div className="hidden md:flex md:w-1/2 bg-zinc-950 text-white relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1696987007764-7f8b85dd3033?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <Link to="/" className="flex items-center" data-testid="admin-login-home-link">
            <img
              src="/logo-white.png"
              alt="IVrenovation and Construction"
              className="h-12 w-auto object-contain"
            />
          </Link>
          <div>
            <p className="overline" style={{ color: "#fb923c" }}>Admin Console</p>
            <h1 className="font-display text-5xl lg:text-6xl tracking-tighter leading-[1.02] mt-4">
              Built with<br />precision.
            </h1>
            <p className="mt-6 text-zinc-400 max-w-md">
              Manage incoming quote requests from the IVrenovation website.
            </p>
          </div>
          <p className="text-xs text-zinc-500 tracking-wider uppercase">© {new Date().getFullYear()} IVrenovation</p>
        </div>
      </div>

      {/* Form */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-12">
        <motion.form
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          onSubmit={submit}
          className="w-full max-w-md"
          data-testid="admin-login-form"
        >
          <div className="flex items-center gap-2 mb-3">
            <Lock className="w-4 h-4 text-orange-600" />
            <span className="overline">Sign in</span>
          </div>
          <h2 className="font-display text-3xl md:text-4xl tracking-tighter text-zinc-950">
            Admin login
          </h2>
          <p className="mt-3 text-zinc-600">Enter your credentials to access the lead inbox.</p>

          <div className="mt-8 flex flex-col gap-5">
            <div className="flex flex-col gap-2">
              <label htmlFor="login-email" className="text-xs font-bold uppercase tracking-wider text-zinc-700">Email</label>
              <input
                id="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-login-email"
                className="border border-zinc-300 bg-white px-4 py-3 text-zinc-900 focus:outline-none focus:border-orange-600 transition"
                autoComplete="email"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label htmlFor="login-password" className="text-xs font-bold uppercase tracking-wider text-zinc-700">Password</label>
              <input
                id="login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-login-password"
                className="border border-zinc-300 bg-white px-4 py-3 text-zinc-900 focus:outline-none focus:border-orange-600 transition"
                autoComplete="current-password"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="mt-8 w-full bg-orange-600 hover:bg-orange-700 disabled:bg-zinc-400 text-white px-8 py-4 text-sm font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 transition"
          >
            {loading ? "Signing in…" : "Sign in"}
            <ArrowUpRight className="w-4 h-4" />
          </button>

          <Link
            to="/"
            className="mt-6 inline-block text-sm text-zinc-500 hover:text-orange-600 transition"
            data-testid="admin-login-back-link"
          >
            ← Back to website
          </Link>
        </motion.form>
      </div>
    </div>
  );
}
